import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, User, ArrowRight } from 'lucide-react';

export default function Welcome() {
  const navigate = useNavigate();
  const { user, login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  useEffect(() => {
    if (user) {
      if (user.onboarding_complete) {
        navigate('/home', { replace: true });
      } else {
        navigate('/onboarding', { replace: true });
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back! 🙏');
      } else {
        await register(formData.email, formData.password, formData.name);
        toast.success('Account created! Let\'s get you started.');
      }
      navigate(isLogin ? '/home' : '/onboarding');
    } catch (error) {
      const message = error.response?.data?.detail || 'Something went wrong';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen sky-gradient flex flex-col"
      data-testid="welcome-page"
    >
      {/* Sky decoration */}
      <div className="absolute top-0 left-0 right-0 h-64 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-32 h-12 bg-white/40 rounded-full blur-xl animate-float" />
        <div className="absolute top-20 right-20 w-40 h-14 bg-white/30 rounded-full blur-xl animate-float" style={{ animationDelay: '1s' }} />
        <div className="absolute top-32 left-1/3 w-24 h-10 bg-white/35 rounded-full blur-xl animate-float" style={{ animationDelay: '2s' }} />
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 relative z-10">
        {/* Logo */}
        <div className="animate-slide-up opacity-0 stagger-1 text-center mb-10">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-white shadow-float mb-6 heartbeat overflow-hidden">
            <img src="/images/logo.png" alt="BlessBe" className="w-24 h-24 object-cover" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 tracking-tight mb-2">
            BlessBe
          </h1>
          <p className="text-lg text-gray-600 max-w-xs mx-auto">
            Spread kindness. Share blessings. Make today better.
          </p>
        </div>

        {/* Auth Card */}
        <Card 
          className="w-full max-w-sm animate-slide-up opacity-0 stagger-2 rounded-3xl border-0 shadow-float bg-white/95 backdrop-blur-sm"
          data-testid="auth-card"
        >
          <CardContent className="p-6">
            <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-full">
              <Button
                type="button"
                variant={isLogin ? "default" : "ghost"}
                className={`flex-1 rounded-full h-10 ${isLogin ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-md' : 'text-gray-600 hover:text-gray-800'}`}
                onClick={() => setIsLogin(true)}
                data-testid="login-tab"
              >
                Sign In
              </Button>
              <Button
                type="button"
                variant={!isLogin ? "default" : "ghost"}
                className={`flex-1 rounded-full h-10 ${!isLogin ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-md' : 'text-gray-600 hover:text-gray-800'}`}
                onClick={() => setIsLogin(false)}
                data-testid="register-tab"
              >
                Sign Up
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-12 h-12 rounded-xl bg-gray-50 border-gray-200 focus:border-blue-400 focus:ring-blue-400"
                    data-testid="name-input"
                  />
                </div>
              )}
              
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-12 h-12 rounded-xl bg-gray-50 border-gray-200 focus:border-blue-400 focus:ring-blue-400"
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-12 h-12 rounded-xl bg-gray-50 border-gray-200 focus:border-blue-400 focus:ring-blue-400"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 rounded-xl text-lg font-semibold bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
                disabled={loading}
                data-testid="submit-button"
              >
                {loading ? (
                  <span className="animate-pulse">Please wait...</span>
                ) : (
                  <>
                    {isLogin ? 'Welcome Back' : 'Start Your Journey'}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-sm text-gray-500 mt-8 animate-fade-in opacity-0 stagger-3 text-center max-w-xs">
          Join thousands spreading kindness and blessings every day
        </p>
      </div>
    </div>
  );
}
